/**
 * The public upload page an end client opens from a BrainBank upload link.
 *
 * Mounted from index.tsx outside every auth and data provider: nobody here has
 * an account. The token is read from the URL fragment (`#t=`), stripped from
 * the address bar, and kept in component state only. A fragment never leaves
 * the browser: not to the CDN's access log, not in any Referer header. The page shows uploading, Uploaded or Failed
 * and nothing about what happens to the file afterwards.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  KeyRound,
  LoaderCircle,
  UploadCloud,
  XCircle,
} from "lucide-react";
import {
  completeDropzoneUpload,
  DropzoneError,
  getDropzone,
  isGate,
  requestDropzoneUpload,
  uploadToS3,
} from "../service/dropzoneService";
import { DropzoneInfo, DropzoneUploadGrant } from "../types";

type PageState = "loading" | "passcode" | "active" | "expired" | "invalid" | "unavailable";
type RowStatus = "queued" | "uploading" | "uploaded" | "failed";

interface Row {
  id: string;
  file: File;
  status: RowStatus;
  progress: number;
  message?: string;
  /** False for failures a retry cannot change: pre-checks, server validation, a dead link. */
  retryable?: boolean;
  /** Kept so a retry within the grant's lifetime reuses the same URL and slot. */
  grant?: DropzoneUploadGrant;
  grantedAt?: number;
  /** The server counted a slot for this file. Never cleared: a retry re-signs the same row. */
  slotTaken?: boolean;
}

export const MAX_IN_FLIGHT = 3;
// The server signs the POST for 300 s; stay well inside that so a retry never
// reuses a policy that expires mid-upload.
export const GRANT_LIFETIME_MS = 4 * 60 * 1000;

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

// The same four types the Upload Files tab accepts, keyed the way
// react-dropzone wants them. The OS file dialog and drag-and-drop filter
// on these, so a client never picks a file the link will refuse.
const ACCEPT_BY_EXTENSION: Record<string, Record<string, string[]>> = {
  pdf: { "application/pdf": [".pdf"] },
  docx: { "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] },
  xlsx: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
  txt: { "text/plain": [".txt"] },
};

export const acceptFor = (extensions: string[]): Record<string, string[]> =>
  Object.assign({}, ...extensions.map((e) => ACCEPT_BY_EXTENSION[e] ?? {}));

const extensionOf = (name: string): string => {
  const base = name.split(/[\\/]/).pop() || "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
};

/** Mirror of the server's validate_file_request; the server message wins when they disagree. */
export const preCheck = (file: File, info: DropzoneInfo): string | null => {
  const ext = extensionOf(file.name);
  const allowed = info.allowed_extensions.map((e) => e.toUpperCase()).join(", ");
  if (!file.name.trim()) return "This file has no name.";
  if (!ext) return `Add a file extension. Upload ${allowed}.`;
  if (!info.allowed_extensions.includes(ext as any)) {
    return `This file type isn't accepted. Upload ${allowed}.`;
  }
  if (file.size < 1) return "This file is empty.";
  if (file.size > info.max_file_bytes) {
    return `${formatBytes(file.size)} is over the ${formatBytes(info.max_file_bytes)} limit. Split the file or ask ${info.requester_name} for a higher limit.`;
  }
  return null;
};

/**
 * Time left in words: "1 day 23 h", "7 h 56 min", "56 min", "1 min 30 s",
 * "45 s". A link can run for two days, so a minutes:seconds clock reads as
 * "2880:00" and tells the client nothing.
 */
export const formatCountdown = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"} ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  if (minutes >= 2) return `${minutes} min`;
  if (minutes === 1) return `1 min ${rest} s`;
  return `${rest} s`;
};

/**
 * Read the token and strip it from the address bar. Called exactly once, from
 * index.tsx, before anything renders. The link carries it as `#t=`; `?t=` is
 * still accepted so the API can fall back to the query form without a UI
 * release if some mail client turns out to drop fragments.
 */
export const readToken = (): string | null => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search);
  const token = hashParams.get("t") || queryParams.get("t");
  if (token) {
    // Drop the token from the address bar before anything else runs.
    hashParams.delete("t");
    queryParams.delete("t");
    const query = queryParams.toString();
    const hash = hashParams.toString();
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`
    );
  }
  return token;
};

interface DropzonePageProps {
  /** The link token, read once by index.tsx. Null when the URL carried none. */
  token: string | null;
}

const DropzonePage: React.FC<DropzonePageProps> = ({ token }) => {
  const [state, setState] = useState<PageState>("loading");
  const [info, setInfo] = useState<DropzoneInfo | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [rows, setRows] = useState<Row[]>([]);
  // The server said the link is full (409); stays set until the page reloads.
  const [serverFull, setServerFull] = useState(false);
  // The passcode the client typed. Memory only; sent as a header with every call.
  const [passcode, setPasscode] = useState("");
  const [passcodeDraft, setPasscodeDraft] = useState("");
  const [passcodeError, setPasscodeError] = useState<string | null>(null);
  const [checkingPasscode, setCheckingPasscode] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const inFlight = useRef(0);

  const load = useCallback(
    async (code?: string) => {
      if (!token) {
        setState("invalid");
        return;
      }
      if (!code) setState("loading");
      try {
        const data = await getDropzone(token, code || undefined);
        if (isGate(data)) {
          setState("passcode");
          return;
        }
        setInfo(data);
        setSecondsLeft(data.seconds_left);
        setPasscodeError(null);
        setState(data.seconds_left > 0 ? "active" : "expired");
      } catch (error) {
        const err = error instanceof DropzoneError ? error : new DropzoneError(0, "");
        if (err.status === 401) {
          // Wrong passcode: stay on the form and say so.
          setPasscodeError(err.message || "Wrong passcode.");
          setState("passcode");
        } else if (err.status === 404) {
          setState("invalid");
        } else {
          setUnavailableMessage(err.status === 429 ? err.message : null);
          setState("unavailable");
        }
      }
    },
    [token]
  );

  useEffect(() => {
    load();
  }, [load]);

  const submitPasscode = async (event: React.FormEvent) => {
    event.preventDefault();
    const code = passcodeDraft.trim();
    if (!code || checkingPasscode) return;
    setCheckingPasscode(true);
    setPasscodeError(null);
    try {
      setPasscode(code);
      await load(code);
    } finally {
      setCheckingPasscode(false);
    }
  };

  // Countdown from the server's seconds_left, never from a wall-clock expiry.
  useEffect(() => {
    if (state !== "active") return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          setState("expired");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [state]);

  const updateRow = useCallback((id: string, patch: Partial<Row>) => {
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const runUpload = useCallback(
    async (row: Row) => {
      if (!token || !info) return;
      inFlight.current += 1;
      updateRow(row.id, { status: "uploading", progress: 0, message: undefined, retryable: undefined });
      const asError = (error: unknown) =>
        error instanceof DropzoneError ? error : new DropzoneError(0, "The upload failed.");
      const linkDied = () => {
        // Expired, revoked, or its BrainBank is gone.
        updateRow(row.id, { status: "failed", message: "This link is no longer active.", retryable: false });
        setState("expired");
      };
      try {
        let grant = row.grant;
        let grantedAt = row.grantedAt ?? 0;
        if (!grant || Date.now() - grantedAt > GRANT_LIFETIME_MS) {
          // Stage 1: ask the API for a presigned POST.
          try {
            grant = await requestDropzoneUpload(token, row.file.name, row.file.size, passcode || undefined);
          } catch (error) {
            const err = asError(error);
            if (err.status === 404) {
              linkDied();
            } else if (err.status === 401) {
              // The passcode stopped being accepted (it cannot change; the link
              // is being guessed at and locked out). Back to the form.
              updateRow(row.id, { status: "failed", message: err.message, retryable: false });
              setPasscodeError(err.message);
              setState("passcode");
            } else if (err.status === 409) {
              // Someone else used the last slots; the limit UI must engage now.
              setServerFull(true);
              updateRow(row.id, { status: "failed", message: err.message, retryable: false });
            } else {
              // 400 is the server's final word on this file; 0, 503 and 5xx are worth a retry.
              updateRow(row.id, { status: "failed", message: err.message, retryable: err.status !== 400 });
            }
            return;
          }
          grantedAt = Date.now();
          updateRow(row.id, { grant, grantedAt, slotTaken: true });
        }

        // Stage 2: the browser posts straight to S3.
        try {
          await uploadToS3(grant.url, grant.fields, row.file, (percent) =>
            updateRow(row.id, { progress: percent })
          );
        } catch (error) {
          const err = asError(error);
          if (err.status === 0) {
            // No HTTP status at all: CORS or network. The only failure the client cannot cause.
            updateRow(row.id, {
              status: "failed",
              message: `Upload blocked. Ask ${info.requester_name} to check their setup.`,
              retryable: true,
            });
          } else {
            // S3 rejected the form (policy expired, condition failed). Drop the
            // grant so the retry asks for a fresh one instead of replaying this.
            updateRow(row.id, {
              status: "failed",
              message: err.message,
              retryable: true,
              grant: undefined,
              grantedAt: undefined,
            });
          }
          return;
        }

        updateRow(row.id, { status: "uploaded", progress: 100 });
        try {
          await completeDropzoneUpload(token, grant.file_id, passcode || undefined);
        } catch {
          // S3 accepted the file; the completion mark is best effort.
        }
      } finally {
        inFlight.current -= 1;
      }
    },
    [token, info, passcode, updateRow]
  );

  // Drain the queue, at most MAX_IN_FLIGHT at once.
  useEffect(() => {
    if (state !== "active") return;
    const queued = rows.filter((r) => r.status === "queued");
    const slots = MAX_IN_FLIGHT - inFlight.current;
    queued.slice(0, Math.max(0, slots)).forEach((row) => runUpload(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, state]);

  // Slots are counted from state, never from a ref: the server's count at load
  // plus every row that has been granted a URL since (the server counted those
  // too). Rows still waiting for a grant are reserved locally so a quick second
  // drop cannot overshoot the limit.
  const grantedCount = rows.filter((r) => Boolean(r.slotTaken)).length;
  const pendingCount = rows.filter(
    (r) => !r.slotTaken && (r.status === "queued" || r.status === "uploading")
  ).length;
  const limitReached = info ? serverFull || info.files_issued + grantedCount >= info.max_files : false;

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[] = []) => {
      if (!info || state !== "active") return;
      const additions: Row[] = [];
      let remaining = serverFull ? 0 : info.max_files - info.files_issued - grantedCount - pendingCount;
      // A file the filter refused still gets a row, so the client learns why
      // instead of watching it vanish.
      rejections.forEach(({ file }) => {
        const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`;
        const problem = preCheck(file, info) ?? `This file type isn't accepted. Upload ${info.allowed_extensions.map((e) => e.toUpperCase()).join(", ")}.`;
        additions.push({ id, file, status: "failed", progress: 0, message: problem });
      });
      accepted.forEach((file) => {
        const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`;
        const problem = preCheck(file, info);
        if (problem) {
          additions.push({ id, file, status: "failed", progress: 0, message: problem });
          return;
        }
        if (remaining <= 0) {
          additions.push({
            id,
            file,
            status: "failed",
            progress: 0,
            message: `This link accepts up to ${info.max_files} files.`,
          });
          return;
        }
        remaining -= 1;
        additions.push({ id, file, status: "queued", progress: 0 });
      });
      setRows((current) => [...current, ...additions]);
    },
    [info, state, serverFull, grantedCount, pendingCount]
  );

  const accept = useMemo(() => (info ? acceptFor(info.allowed_extensions) : undefined), [info]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    noClick: true,
    disabled: state !== "active" || limitReached,
  });

  const retry = (row: Row) => {
    if (state !== "active") return;
    updateRow(row.id, { status: "queued", progress: 0, message: undefined });
  };

  const rulesLine = useMemo(() => {
    if (!info) return "";
    const types = info.allowed_extensions.map((e) => e.toUpperCase()).join(", ");
    return `${types} · Up to ${formatBytes(info.max_file_bytes)} each · Up to ${info.max_files} files`;
  }, [info]);

  const uploadedCount = rows.filter((r) => r.status === "uploaded").length;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Helmet>
        <title>Upload files</title>
        <meta name="referrer" content="no-referrer" />
        <meta name="robots" content="noindex" />
      </Helmet>
      <main className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-12">
        {state === "loading" && (
          <div className="flex min-h-[70vh] items-center justify-center" role="status" aria-live="polite">
            <LoaderCircle className="h-8 w-8 animate-spin text-violet-600" />
            <span className="sr-only">Loading</span>
          </div>
        )}

        {state === "invalid" && (
          <Notice
            icon={<XCircle className="h-8 w-8 text-red-500" />}
            title="This link isn't valid"
            body="The link may have been revoked or copied incorrectly. Check the full link in the message you received, or ask the sender for a new one. If you reloaded this page, open the link from your message again."
          />
        )}

        {state === "passcode" && (
          <form onSubmit={submitPasscode} className="mx-auto max-w-sm text-center py-12" aria-label="Enter passcode">
            <KeyRound className="mx-auto h-8 w-8 text-violet-600" />
            <h1 className="mt-4 text-lg font-semibold text-gray-900">This link needs a passcode</h1>
            <p className="mt-2 text-sm text-gray-600">
              The person who sent you the link shared it separately, for example by phone or text.
            </p>
            <label htmlFor="dropzone-passcode" className="sr-only">
              Passcode
            </label>
            <input
              id="dropzone-passcode"
              type="password"
              autoComplete="off"
              autoFocus
              value={passcodeDraft}
              onChange={(e) => setPasscodeDraft(e.target.value)}
              className="mt-6 w-full rounded-md border border-gray-300 px-3 py-2 text-center text-sm focus:border-violet-500 focus:ring-violet-500"
            />
            {passcodeError && (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {passcodeError}
              </p>
            )}
            <button
              type="submit"
              disabled={!passcodeDraft.trim() || checkingPasscode}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-50"
            >
              {checkingPasscode && <LoaderCircle className="h-4 w-4 animate-spin" />}
              Continue
            </button>
          </form>
        )}

        {state === "unavailable" && (
          <Notice
            icon={<AlertCircle className="h-8 w-8 text-amber-500" />}
            title="Try again in a moment"
            body={unavailableMessage || "The upload service didn't answer. Your link is still valid until it expires."}
            action={
              <button
                type="button"
                onClick={() => load(passcode || undefined)}
                className="mt-4 inline-flex items-center rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
              >
                Retry
              </button>
            }
          />
        )}

        {state === "expired" && (
          <Notice
            icon={<Clock className="h-8 w-8 text-amber-500" />}
            title="This upload link has expired"
            body={`Links stay open for a limited time. Ask ${info?.requester_name ?? "the sender"} to send you a new link.${
              uploadedCount > 0 ? " Files you already uploaded were received." : ""
            }`}
          />
        )}

        {state === "active" && info && (
          <div className="space-y-6">
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{info.requester_name}</span> requests documents
                </p>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 truncate">{info.label}</h1>
                <p className="text-sm text-gray-500 pt-1">{rulesLine}</p>
                {info.hold_for_approval && (
                  <p className="text-sm text-gray-500">
                    {info.requester_name} reviews each file before it is used.
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs uppercase tracking-wide text-gray-500">Link expires in</p>
                <p
                  className={`text-lg font-semibold tabular-nums ${secondsLeft < 120 ? "text-red-600" : "text-gray-900"}`}
                  aria-live="off"
                >
                  {formatCountdown(secondsLeft)}
                </p>
              </div>
            </header>

            <div
              {...getRootProps({
                className: `rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                  limitReached
                    ? "border-gray-200 bg-gray-100 text-gray-400"
                    : isDragActive
                    ? "border-violet-500 bg-violet-50"
                    : "border-gray-300 bg-white hover:border-violet-400"
                }`,
              })}
            >
              <input {...getInputProps()} aria-label="Choose files" />
              <UploadCloud className="mx-auto h-8 w-8 text-violet-600" />
              {limitReached ? (
                <p className="mt-3 text-sm">This link has reached its file limit.</p>
              ) : (
                <>
                  <p className="mt-3 text-sm text-gray-700">Drag files here</p>
                  <p className="text-xs text-gray-500 my-1">or</p>
                  <button
                    type="button"
                    onClick={open}
                    className="inline-flex items-center rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                  >
                    Choose files
                  </button>
                </>
              )}
            </div>

            {rows.length > 0 && (
              <ul className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white">
                {rows.map((row) => (
                  <li key={row.id} className="flex items-start gap-3 px-4 py-3">
                    <FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-gray-900">{row.file.name}</span>
                        <span className="flex-shrink-0 text-xs text-gray-500">{formatBytes(row.file.size)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        {row.status === "queued" && <span className="text-gray-500">Waiting</span>}
                        {row.status === "uploading" && (
                          <>
                            <LoaderCircle className="h-3.5 w-3.5 animate-spin text-violet-600" />
                            <span className="text-violet-700">Uploading {row.progress}%</span>
                          </>
                        )}
                        {row.status === "uploaded" && (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-green-700">Uploaded</span>
                          </>
                        )}
                        {row.status === "failed" && (
                          <>
                            <XCircle className="h-3.5 w-3.5 text-red-500" />
                            <span className="text-red-700">Failed</span>
                            {row.message && <span className="text-gray-600">· {row.message}</span>}
                            {row.retryable && (
                              <button
                                type="button"
                                onClick={() => retry(row)}
                                className="ml-1 font-medium text-violet-700 hover:text-violet-800"
                              >
                                Retry
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      {row.status === "uploading" && (
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-gray-200">
                          <div
                            className="h-full bg-violet-600 transition-all"
                            style={{ width: `${row.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-xs text-gray-500">
              Files go only to {info.requester_name}&apos;s Kyva workspace. You can close this page once every
              file shows Uploaded.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

const Notice: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}> = ({ icon, title, body, action }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
    <div className="flex justify-center">{icon}</div>
    <h1 className="mt-4 text-lg font-semibold text-gray-900">{title}</h1>
    <p className="mt-2 text-sm text-gray-600">{body}</p>
    {action}
  </div>
);

export default DropzonePage;
